import React from 'react';
import {
  CommonButton,
  HeaderCard,
  ListSkeleton,
  MediaModalGroup,
  UploadMediaView,
} from '../../components';
import { TouchableOpacity } from '../../common';
import { usePaymentEditHandlers } from '../hooks';
import { InvoiceCard } from '../../invoice';
import { ReactComponent as RightArrow } from '@zonofi/svg-icons/arrow_right.svg';
import {
  FileUploadOutput,
  Invoice,
  PaymentDetailsMap,
  dispatch,
  getSelectedItems,
  updateSelectedItemsMap,
  useAppSelector,
} from '@zonofi/zono-money-store';
import { SVGS } from '../../assets';
import { PaymentModeType } from '../../constants';
import { useParams } from 'react-router-dom';
import { isSaveButtonDisabled } from '../helper';
import './payment-edit-screen-content.scss';

interface PaymentEditScreenContentProps {
  paymentDetails: PaymentDetailsMap;
  setPaymentDetails: React.Dispatch<React.SetStateAction<PaymentDetailsMap>>;
  paymentState: {
    paymentMode: PaymentModeType | null;
    paymentType: string;
  };
  setPaymentState: React.Dispatch<
    React.SetStateAction<{
      paymentMode: PaymentModeType | null;
      paymentType: string;
    }>
  >;
  uploadedMedia: FileUploadOutput[];
  setUploadedMedia: React.Dispatch<React.SetStateAction<FileUploadOutput[]>>;
  onSelectInvoices?: () => void;
  isOnline?: boolean;
}

export const PaymentEditScreenContent: React.FC<
  PaymentEditScreenContentProps
> = (props) => {
  const { paymentId = '', customerId = '' } = useParams();
  const selectedInvoiceItems = useAppSelector(getSelectedItems);

  const {
    selectedMedia,
    activeModal,
    setActiveModal,
    onMediaPress,
    onThreeDotPress,
    mediaActions,
    uploadMediaActions,
    imageParseState,
    renderPaymentFields,
    saveLoader,
    onSavePayment,
  } = usePaymentEditHandlers(props);

  return (
    <>
      <div className="edit-payment-container">
        {/* Upload Media */}
        <div className="payment-upload-media-container">
          <HeaderCard
            title="UPLOAD PAYMENT RECEIPT"
            subtitle="Upload your payment receipts. We accept JPG, PDF, PNG upto 5MB"
          />

          <UploadMediaView
            uploadedMedia={props.uploadedMedia}
            selectedMedia={selectedMedia}
            onUploadPress={() => setActiveModal('MEDIA_UPLOAD')}
            onMediaPress={onMediaPress}
            onThreeDotPress={onThreeDotPress}
            imageIdToParse={imageParseState?.imageIdToParse}
          />
        </div>

        {/* Parsed Fields */}
        <div className="payment-field-container">
          <HeaderCard
            title="PARSED PAYMENT DETAILS"
            subtitle="The information below is pre-populated from the uploaded image"
          />

          <div className="payment-details-content">
            {imageParseState.shouldParseImage ? (
              <ListSkeleton
                height={50}
                className="payment-list-skeleton-view"
              />
            ) : (
              renderPaymentFields
            )}
          </div>
        </div>

        {/* Select Invoice */}
        <div className="payement-invoice-container">
          <HeaderCard
            title="SELECT INVOICES"
            subtitle="Link payment to outstanding invoices"
          />

          <TouchableOpacity
            className="payment-invoice-content"
            onPress={props.onSelectInvoices}
          >
            <div className="payment-invoice-content--view">
              <img src={SVGS.InvoiceBlack} />
              <span className="payment-invoice-content--text font-medium">
                Select Invoices
              </span>
            </div>
            <RightArrow />
          </TouchableOpacity>

          {selectedInvoiceItems?.length > 0 && (
            <div className="payment-invoice-list-content">
              {selectedInvoiceItems.map((invoice: Invoice) => (
                <InvoiceCard
                  key={invoice.id}
                  invoiceItem={invoice}
                  showDeletion
                  showShare={false}
                  onDeletePress={(item) =>
                    dispatch(updateSelectedItemsMap(item))
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Save */}
      <div className="payment-save-button">
        <CommonButton
          text={paymentId || customerId ? 'Save' : 'Upload Payment'}
          onPress={onSavePayment}
          loading={saveLoader}
          spinnerColor="white"
          buttonStyle="payment-save-button-view"
          disabled={
            imageParseState?.shouldParseImage ||
            isSaveButtonDisabled(
              props.paymentDetails,
              props.paymentDetails?.paymentMode as PaymentModeType
            )
          }
        />
      </div>

      {/* Modals */}
      <MediaModalGroup
        activeModal={activeModal}
        selectedMediaItem={selectedMedia}
        onClose={() => setActiveModal('NONE')}
        mediaActions={mediaActions}
        uploadMediaActions={uploadMediaActions}
      />
    </>
  );
};
